import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, debounceTime, filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { cloneDeep, get, isEmpty, isEqual } from 'lodash';
import { FileSaverService } from 'ngx-filesaver';
import { Title } from '@angular/platform-browser';
import { Store } from '@ngrx/store';

import { CommonModalsActionsEnum, DangerModalComponent, InfoModalComponent, WarningModalComponent } from 'common/src/modules/modals/modals-common';
import { NavBarBtnModel, PaginationModel, UIStatesEnum } from 'common/src/models';
import { TableSummaryBarItemModel } from 'common/src/modules/ui-components/table-summary-bar/table-summary-bar.model';
import { TabDefinitionModel } from 'common/src/modules/ui-components/nav-tabs/tab-definition.model';
import { TableActivateTypes } from 'common/src/modules/ui-components/table/custom-table.enums';
import { FileUploadParams } from 'common/src/models/file-upload-params.model';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { FileService } from 'common/src/services/file.service';
import { TableColumnModel, TableColumnModelExtended } from 'common/src/models/table-column.model';
import { STATUS_KEY } from './purchase-order-list.constants';
import { PoTabs } from '../../enums';
import { PoMainFilePreviewModel, PurchaseOrder, PurchaseOrderCounters } from '../../models';
import { PurchaseOrderApiService } from '../../services/purchase-order-api.service';
import { FilterModel } from '../../../warehouse/models/filter.model';
import { ResponseList, ResponseModel, UserSorting } from '../../../shared/models';
import { TradePositionModel } from '../../../trade/models';
import { TradeOfferPositionsByProductType } from '../../../trade-offer/models';
import { DocumentPdfPreviewProgressItemModel } from '../../../shared/components/document-pdf-preview/document-pdf-preview.component';
import {
  ActionBarButtons,
  getDeleteModalData,
  getDeletePermanentlyModalData,
  getPurchaseOrderListFiltersConfig,
  getPurchaseOrderListInnerTableColumns,
  getTableSummaryBarItems,
  PurchaseOrderListColumns,
  PurchaseOrderTabs
} from './purchase-order-list.config';
import { getConfigColumns } from './config-columns';
import { DocumentsTableColumnModel, MenuPanelComponent, MenuPanelItemModel } from '../../../shared/components';
import { POListState } from '../../store/reducers';
import { AppState } from '../../../store/state/app.state';
import { selectPOList } from '../../store/selectors';
import { DEFAULT_PAGINATION, DEFAULT_SORT_DIRECTION, STATUS_LABEL_CLASS } from '../../../shared/constants';
import { getContentDispositionFileName } from 'common/src/modules/rnpl-common/helpers';
import { FiltersGroupModel, FiltersService } from 'common/src/modules/filters';
import { StatusTranslatePipe, UnitTypeTranslatePipe } from 'common/src/modules/rnpl-common';
import {
  BulkStatusToastAction,
  getBulkDocTypeName,
  getBulkResponseCounts,
  showBulkStatusToast,
  withBulkActionSpinner,
  collapseMoreActionsIntoFooter,
  getEditOnlyPreviewFooterActions,
  buildRowContextMenuItems,
  getDocumentUrl,
  getDocumentCreatedToastKey,
  ActivatedRowReselectEnum,
  getReselectedActivatedRow
} from '../../../shared/helpers';
import { DocumentTypesUppercaseEnum } from 'common/src/modules/modals/modals-common/link-document-modal/enums/ducument-types.enum';
import { UserSortingService } from '../../../shared/services';
import { CompanyProfile } from '../../../administration/models/company-profile.model';
import { selectCompanyProfile } from '../../../administration/store/selectors';
import { ContextMenuItemAction, ContextMenuItemModel } from 'common/src/models/context-menu.model';
import { FlagsEnum } from 'common/src/models/flags.enum';
import { CreateSoModalComponent } from 'common/src/modules/modals/modals-purchase-order';
import { IncomingDeliveryProductsReceiptModalComponent } from 'common/src/modules/modals/modals-incoming-delivery';
import { IncomingInvoiceApiService } from '../../../incoming-invoice/services/invoice-api.service';
import { CannotProceedErrModalData } from '../purchase-order-management/purchase-order-management.config';
import { SalesOrderModel } from '../../../sales-order/models/sales-order.model';
import { DeleteTemplateModalData } from '../../../shared/constants';
import {
  getPurchaseOrderBillingIconClass,
  getPurchaseOrderDeliveryIconClass,
  getEmptyPurchaseOrderStatusTooltip,
  mapPurchaseOrderStatusTooltip,
  PO_BILLING_TOOLTIP_FIELDS,
  PO_DELIVERY_TOOLTIP_FIELDS,
} from '../../helpers';
import {
  PurchaseOrderBillingTooltipResponse,
  PurchaseOrderDeliveryTooltipResponse,
  PurchaseOrderStatusTooltipColumn,
  PurchaseOrderStatusTooltipStateModel,
  PurchaseOrderStatusTooltipViewModel,
} from '../../models';

const STATUS_TOOLTIP_HOVER_DELAY_MS = 350;
const STATUS_TOOLTIP_HALF_WIDTH_PX = 132;
const STATUS_TOOLTIP_VIEWPORT_MARGIN_PX = 8;
const STATUS_TOOLTIP_MAX_DOCUMENTS = 5;
const STATUS_TOOLTIP_MAX_HEIGHT_PX = 280;

@Component({
  selector: 'rnpl-purchase-order-list',
  templateUrl: './purchase-order-list.component.html',
  styleUrls: ['./purchase-order-list.component.scss'],
  providers: [FiltersService]
})
export class PurchaseOrderListComponent implements OnInit, OnDestroy {
  public companyProfile: CompanyProfile;
  public tabs: TabDefinitionModel[] = PurchaseOrderTabs;
  public columns: DocumentsTableColumnModel[] = getConfigColumns();
  public columnsConfig: DocumentsTableColumnModel[] = [];
  public oldColumns: TableColumnModel[] = [];
  public innerTableColumns: TableColumnModel[] = getPurchaseOrderListInnerTableColumns();
  public pagination: PaginationModel = DEFAULT_PAGINATION;
  public actionBarButtons: NavBarBtnModel[] = withBulkActionSpinner(ActionBarButtons);
  public tableSummaryBarItems: TableSummaryBarItemModel[] = [];
  public selectedRows = [];
  public documentTypesUppercaseEnum = DocumentTypesUppercaseEnum;
  public statusLabelClasses = STATUS_LABEL_CLASS;
  public poTabs = PoTabs;
  public flagsEnum = FlagsEnum;

  private _activatedRow: PurchaseOrder;
  public get activatedRow(): PurchaseOrder { return this._activatedRow; }
  public set activatedRow(row: PurchaseOrder) {
    this._activatedRow = row;
    this.previewFooterActionsForStatus = this.computePreviewFooterActionsForStatus();
    this.rowContextMenuItems = buildRowContextMenuItems(
      this.previewFooterActionsForStatus, this.purchaseOrderPreviewActions, this.hasEditingFlag(row) || this.hasCreationFlag(row)
    );
    this.loadPreviewProgressItems(row);
    this.loadPreviewFile(row);
  }

  public rowContextMenuItems: MenuPanelItemModel[] = [];
  @ViewChild('rowContextMenuRef', { static: false }) rowContextMenuRef: MenuPanelComponent;

  public previewProgressItems: DocumentPdfPreviewProgressItemModel[] = [];
  public previewProgressLoading = false;

  public previewFile: PoMainFilePreviewModel | null = null;
  public previewFileLoading = false;
  private previewFileRequestedId: number | null = null;

  public previewFooterActions: NavBarBtnModel[] = [
    {
      classes: 'btn-white',
      text: 'BUTTON.MORE',
      iconName: 'menu',
      actionName: 'moreActions',
      isMoreAction: true,
      withActions: true,
      disabledMainAction: true,
      yPosition: 'above',
      backdropClass: 'footer-dropdown-menu',
      actions: [],
    },
    {
      classes: 'btn-white',
      text: 'DOC_SUMMARY.CONFIRM_GOODS_RECEIPT',
      iconName: 'goods-receipt',
      actionName: 'onPreviewProductReceiptClick',
      withActions: true,
      disabledMainAction: false,
      yPosition: 'above',
      backdropClass: 'footer-dropdown-menu',
      actions: [
        { actionTitle: 'DOC_SUMMARY.CONFIRM_GOODS_RECEIPT', actionName: 'onPreviewProductReceiptClick', actionIcon: 'goods-receipt' },
        { actionTitle: 'BUTTON.CREATE_INVOICE', actionName: 'onPreviewCreateInvoiceClick', actionIcon: 'incoming-invoice' },
      ]
    },
  ];

  public previewFooterActionsForStatus: NavBarBtnModel[] = this.previewFooterActions;
  private autoSelectFirstRow = true;
  private activatedRowReselect: ActivatedRowReselectEnum = ActivatedRowReselectEnum.NEIGHBOR;

  public sort: FilterModel = { nameColumn: 'updatedAt', value: DEFAULT_SORT_DIRECTION };
  public filtersConfig: FiltersGroupModel[] = [];

  readonly isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly isLoadingMegaHint$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly isLoadingInnerTable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  readonly poList$: BehaviorSubject<PurchaseOrder[]> = new BehaviorSubject([]);
  readonly activeStatus$: BehaviorSubject<PoTabs> = new BehaviorSubject<PoTabs>(PoTabs.Draft);
  readonly updatedAt$: BehaviorSubject<Date> = new BehaviorSubject<Date>(new Date());
  readonly selectedInnerTableData$: BehaviorSubject<TradePositionModel[]> = new BehaviorSubject([]);
  readonly destroy$: Subject<void> = new Subject();
  private listData$: BehaviorSubject<POListState> = new BehaviorSubject(null);
  private readonly fetchListTrigger$: Subject<any> = new Subject<any>();
  readonly exportCsvRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  readonly createPoRequest$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  readonly btnToClearLoadingStatus$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  readonly previewConfirmReceiptRequest$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  readonly previewLinkIncomingInvoiceRequest$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  @ViewChild('tableComponent', {static: false}) tableComponentRef;
  @ViewChild('filters', {static: false}) filtersRef;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly toasterService: ToasterService,
    private readonly router: Router,
    private readonly poApi: PurchaseOrderApiService,
    private readonly fileService: FileService,
    private readonly fileSaverService: FileSaverService,
    private readonly dialog: MatDialog,
    private readonly titleService: Title,
    private readonly store: Store<AppState>,
    private readonly cdr: ChangeDetectorRef,
    private readonly statusTranslatePipe: StatusTranslatePipe,
    private readonly unitTypeTranslatePipe: UnitTypeTranslatePipe,
    private readonly translate: TranslateService,
    public readonly filtersService: FiltersService,
    private readonly userSortingService: UserSortingService,
    private readonly invoiceApiService: IncomingInvoiceApiService
  ) {
    this.store.select(selectCompanyProfile)
      .pipe(takeUntil(this.destroy$))
      .subscribe((companyProfile: CompanyProfile) => {
        this.companyProfile = companyProfile;
      });
  }

  ngOnInit(): void {
    this.setTitle();
    this.bindPOList();
    this.initStatusTooltipStream();
    this.bindFetchListStream();

    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: ParamMap) => {
        const pageFromRouting = Number(params.get('page'));

        if (this.activeStatus !== params.get(STATUS_KEY)) {
          this.sort = { nameColumn: 'updatedAt', value: DEFAULT_SORT_DIRECTION };
          if (this.filtersRef) {
            this.filtersRef.closeSidebar();
          }
          this.autoSelectFirstRow = true;
          this.activatedRow = null;
        }
        this.activeStatus$.next(params.get(STATUS_KEY) as PoTabs);
        this.filtersConfig = getPurchaseOrderListFiltersConfig(
          this.activeStatus,
          this.companyProfile,
          this.poApi.getPurchaseOrdersFilterExternalNumbers(),
          this.poApi.getPurchaseOrdersRunpleIds(this.activeStatus),
          this.poApi.getDocumentTemplatesNames(),
          this.poApi.getPurchaseOrdersFiltersBranchOffice(this.activeStatus),
        );
        this.selectedRows = [];
        this.tableSummaryBarItems = [];
        this.oldColumns = this.filterColumns(PurchaseOrderListColumns);
        this.columns = this.getColumnsForStatus(this.activeStatus);
        this.pagination = { ...this.pagination, page: pageFromRouting };

        this.getPOList();
        this.getPOTabsCounters();
        this.cdr.detectChanges();
      });
  }

  public setTitle(): void {
    const activeStatus = this.translate.instant(this.statusTranslatePipe.transform(this.activeStatus));
    this.titleService.setTitle(
      `${this.translate.instant('APP.PURCHASE_ORDERS')}: ${activeStatus}`
    );
  }

  public bindPOList() {
    this.store.select(selectPOList)
      .pipe(takeUntil(this.destroy$))
      .subscribe((purchaseOrderList) => {
        this.listData$.next(purchaseOrderList);
      });
  }

  public getPOList(preventLoading = false, page = this.pagination.page): void {
    this.isLoadingMegaHint$.next(true);

    if (!preventLoading) {
      this.isLoading$.next(true);
    }

    if (this.tableComponentRef) {
      this.tableComponentRef.collapseAll();
    }

    this.selectedRows = [];
    this.clearStatusTooltipCache();

    this.fetchListTrigger$.next(page);
  }

  public onSyncRefresh(): void {
    this.activatedRowReselect = ActivatedRowReselectEnum.NONE;
    this.getPOList(true);
    this.getPOTabsCounters();
  }

  public onSearchChange(value: string): void {
    this.filtersService.setSearchValue(value);
    this.resetPageAndFetch();
  }

  public resetPageAndFetch(): void {
    this.activatedRowReselect = ActivatedRowReselectEnum.NONE;
    if (this.pagination.page !== 1) {
      this.goToPage(0);
    } else {
      this.getPOList(false);
    }
  }

  private bindFetchListStream(): void {
    this.fetchListTrigger$
      .pipe(
        switchMap((page) => this.buildPOListRequest$(page)),
        takeUntil(this.destroy$)
      )
      .subscribe((data: ResponseList<PurchaseOrder> | null) => {
        if (!data || (data.status && data.status !== this.activeStatus)) { return; }

        const previousList = this.poList$.getValue();
        this.poList$.next(this.preparePOList(data.data));
        this.updateActivatedRow(previousList, this.poList$.getValue());
        this.tableSummaryBarItems = getTableSummaryBarItems(data.totals, this.activeStatus);
        if (this.activeStatus !== PoTabs.All) {
          this.tabs.find(tab => tab.tabName === data.status).count = data.totals.totalCount;
        }

        this.pagination = get(data, 'pagination', DEFAULT_PAGINATION);
        this.isLoading$.next(false);
        this.isLoadingMegaHint$.next(false);
        this.updatedAt$.next(new Date());

        this.setTitle();
      });
  }

  private buildPOListRequest$(page): Observable<ResponseList<PurchaseOrder> | null> {
    return this.userSortingService.getUserSorting(this.activeStatus, DocumentTypesUppercaseEnum.PO)
      .pipe(
        tap((sorting: UserSorting) => {
          this.sort = {
            nameColumn: sorting.sortBy || this.sort.nameColumn,
            value: sorting.direction || this.sort.value
          };
          const purchaseOrdersByCurrentPage = this.listData$.getValue()[this.activeStatus][page];
          if (purchaseOrdersByCurrentPage
            && isEqual(this.sort, purchaseOrdersByCurrentPage.sort)
            && isEqual(this.filtersService.searchValue || null, purchaseOrdersByCurrentPage.search || null)) {
            this.pagination = purchaseOrdersByCurrentPage.pagination;
            this.sort = purchaseOrdersByCurrentPage.sort;
            this.poList$.next(this.preparePOList(purchaseOrdersByCurrentPage.data));
            this.isLoading$.next(false);
          }
        }),
        switchMap(() => this.poApi.getPoList(
          this.activeStatus,
          page,
          this.pagination.per_page,
          this.sort,
          { ...this.filtersService.getFilters(), ...(this.filtersService.searchValue ? { search: this.filtersService.searchValue } : {}) }
        )),
        catchError((error) => {
          this.isLoading$.next(false);
          this.isLoadingMegaHint$.next(false);
          this.updatedAt$.next(null);
          this.showMsg('error', error.error.message);
          return of(null);
        })
      );
  }

  public getPOTabsCounters(): void {
    this.poApi.getPOListCounters()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: PurchaseOrderCounters) => {
        this.tabs = this.tabs.map((t: TabDefinitionModel) => {
          if (!isEmpty(this.filtersService.getFilters()) && t.tabName === this.activeStatus) { return t; }
          return {...t, count: response[t.tabName]};
        });
      });
  }

  private filterColumns(cols: TableColumnModelExtended[]): TableColumnModelExtended[] {
    const columns = [...cols];

    return columns.filter((col: TableColumnModelExtended) => {
      if (!col.showWithStatuses) { return true; } // show for all statuses
      return col.showWithStatuses.includes(this.activeStatus); // check for showing
    });
  }

  private getColumnsForStatus(status: PoTabs): DocumentsTableColumnModel[] {
    return getConfigColumns(this.companyProfile).filter((col: DocumentsTableColumnModel) => {
      if (col.showWithStatuses) {
        return col.showWithStatuses.includes(status);
      }
      return true;
    });
  }

  public rowClick(row: PurchaseOrder): void {
    this.activatedRow = row;
  }

  public rowCommands = (row: PurchaseOrder): any[] => {
    const url = getDocumentUrl(DocumentTypesUppercaseEnum.PO, row.id);
    return url ? [url] : [];
  };

  public onRowContextMenu({ row, event }: { row: PurchaseOrder; event: MouseEvent }): void {
    event.preventDefault();
    event.stopPropagation();
    this.activatedRow = row;
    if (this.rowContextMenuItems.length) {
      this.rowContextMenuRef.openAt(event);
    }
  }

  public onRowContextMenuItemSelected(item: MenuPanelItemModel): void {
    this.onPreviewActionEmitted(item.id);
  }

  private updateActivatedRow(previousList: PurchaseOrder[], list: PurchaseOrder[]): void {
    const reselect = this.activatedRowReselect;
    this.activatedRowReselect = ActivatedRowReselectEnum.NEIGHBOR;

    if (this.autoSelectFirstRow) {
      this.activatedRow = list.length ? list[0] : null;
      this.autoSelectFirstRow = false;
      return;
    }

    const previouslyActivatedId = this.activatedRow ? this.activatedRow.id : null;
    const nextRow = this.activatedRow
      ? list.find(po => po.id === this.activatedRow.id)
        || getReselectedActivatedRow(reselect, previousList, list, this.activatedRow, po => po.id)
      : null;

    if (previouslyActivatedId && nextRow && nextRow.id === previouslyActivatedId) {
      this.previewFileRequestedId = null;
    }

    this.activatedRow = nextRow;
  }

  private loadPreviewFile(row: PurchaseOrder): void {
    if (!row) {
      this.previewFileRequestedId = null;
      this.previewFile = null;
      this.previewFileLoading = false;
      return;
    }
    if (this.previewFileRequestedId === row.id) { return; }

    const requestedId = row.id;
    this.previewFileRequestedId = requestedId;
    this.previewFile = null;
    this.previewFileLoading = true;

    this.poApi.getPurchaseOrderMainFile(requestedId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((file: PoMainFilePreviewModel | null) => {
        if (this.previewFileRequestedId !== requestedId) { return; }
        this.previewFile = file || null;
        this.previewFileLoading = false;
        this.cdr.detectChanges();
      }, () => {
        if (this.previewFileRequestedId !== requestedId) { return; }
        this.previewFile = null;
        this.previewFileLoading = false;
        this.cdr.detectChanges();
      });
  }

  private loadPreviewProgressItems(row: PurchaseOrder): void {
    this.previewProgressItems = [];
    this.previewProgressLoading = this.showBillingDeliveryIndicators(row);
    if (!this.showBillingDeliveryIndicators(row)) { return; }

    this.poApi.getPurchaseOrderPositions(row.id)
      .pipe(
        finalize(() => {
          if (this.activatedRow && this.activatedRow.id === row.id) {
            this.previewProgressLoading = false;
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response: ResponseModel<TradeOfferPositionsByProductType>) => {
        if (!this.activatedRow || this.activatedRow.id !== row.id) { return; }

        const totals: any = get(response, 'totals');
        if (!totals) { return; }

        const items: DocumentPdfPreviewProgressItemModel[] = [];

        if (totals.positionsToReceive) {
          items.push({
            label: 'PO.RECEIVED_POSITIONS',
            value: totals.receivedPositions || 0,
            maxValue: totals.positionsToReceive,
          });
        }
        if (totals.positionsToPay) {
          items.push({
            label: 'PO.PAID_POSITIONS',
            value: totals.paidPositions || 0,
            maxValue: totals.positionsToPay,
          });
        }

        this.previewProgressItems = items;
      }, () => {});
  }

  createNewPurchaseOrder(): void {
    if (this.createPoRequest$.getValue()) { return; }
    this.createPoRequest$.next(true);

    this.poApi.createDefaultPurchaseOrder()
      .pipe(
        catchError(error => {
          this.createPoRequest$.next(false);
          return throwError(error);
        }),
        tap((data) => {
          this.router.navigate([`/trade/purchase-order/${data.id}`]);
        }),
        takeUntil(this.destroy$),
      ).subscribe();
  }

  private preparePOList(poList: PurchaseOrder[]): PurchaseOrder[] {
    poList = cloneDeep(poList); // If data is loading from store - object is locked for edit
    return poList.map((po: PurchaseOrder) => {

      if (po.positionsConfirmedAt) {
        po.deliveryDateValue = {
          replaceWithDate: true,
          date: get(po, 'positionsConfirmedAt', null)
        };
      } else {
        po.deliveryDateValue = {
          expectedDate: po.estimatedDeliveryDate,
          replaceWithEmpty: !po.estimatedDeliveryDate,
          date: get(po, 'createdAt', null)
        };
      }

      if (po.status === 'template' && po.name) {
        po.templateNameLink = {
          label: po.name,
          routerLink:`/trade/purchase-order/${po.id}`
        };
      }

      po.documentLink = {
        label: po.runpleId,
        routerLink:`/trade/purchase-order/${po.id}`
      };

      if (po.status) {
        po['status_data'] = this.hasEditingFlag(po) ? FlagsEnum.EDITING : po.status;
      }

      return po;
    });
  }

  private prepareInnerTableColumns(positions: TradePositionModel[]): TradePositionModel[] {
    return positions.map((pos) => ({
      ...pos,
      productLink: {
        label: pos.productRunpleId,
        routerLink: `/products/product-view/${pos.productId}`
      },
      productNameLink: {
        label: pos.description,
        routerLink: `/products/product-view/${pos.productId}`
      },
      openData: {
        value: pos.open,
        maxValue: pos.quantity,
        inverse: true,
        postfix: this.unitTypeTranslatePipe.transform(pos.unitType),
      },
    }))
  }

  public onActionReceived(btnAction: string) {
    if (this[btnAction]) {
      this[btnAction]();
    }
  }

  rowClickReceiver(event): void {
    if (event.type === TableActivateTypes.RowDetail) {
      this.isLoadingInnerTable$.next(true);

      this.poApi.getPurchaseOrderListPositions(event.row.id)
        .pipe(
          finalize(() => this.isLoadingInnerTable$.next(false)),
          takeUntil(this.destroy$)
        )
        .subscribe((positions: TradePositionModel[]) => {
          this.selectedInnerTableData$.next(this.prepareInnerTableColumns(positions));
        });
    }

    if (event.type === TableActivateTypes.Link) {
      this.router.navigate([`/trade/purchase-order/${event.row.id}`]);
    }
  }

  onManagePO(e: PurchaseOrder): void {
    this.router.navigate([`/trade/purchase-order/${e.id}`]);
  }

  public onExportCsvClick(): void {
    if (this.exportCsvRequest$.getValue()) { return; }
    this.exportCsvRequest$.next(true);

    const request$: Observable<FileUploadParams> = this.activeStatus === PoTabs.All
      ? this.poApi.getPOListExportAll()
      : this.poApi.getPOListExport(this.activeStatus);

    request$
      .pipe(takeUntil(this.destroy$))
      .subscribe((fileParams: FileUploadParams) => this.downloadFile(fileParams.url));
  }

  public downloadFile(url: string) {
    this.fileService.downloadFile(url)
      .pipe(
        finalize(() => this.exportCsvRequest$.next(false)),
        takeUntil(this.destroy$),
      )
      .subscribe((res: any) => {
        this.fileSaverService.save(
          res.body,
          getContentDispositionFileName(res.headers.get('Content-Disposition')) || this.activeStatus
        );
      });
  }

  private changePOStatus(status: PoTabs, btnName: string): void {
    const ids = this.bulkActionableSelectedRows.map((po: PurchaseOrder) => po.id);
    if (!ids.length) {
      this.btnToClearLoadingStatus$.next(btnName);
      return;
    }

    const action: BulkStatusToastAction = status === PoTabs.Deleted ? 'DELETED'
      : status === PoTabs.Open ? 'PROCEEDED'
      : status === PoTabs.Canceled ? 'CANCELED'
      : status === PoTabs.Draft ? 'RESTORED'
      : null;

    this.poApi.changePOStatusBatch(status, ids)
      .pipe(
        finalize(() => this.btnToClearLoadingStatus$.next(btnName)),
        takeUntil(this.destroy$)
      )
      .subscribe((res: any) => {
        this.selectedRows = [];
        this.getPOTabsCounters();
        this.getPOList(true);

        if (action) {
          const counts = getBulkResponseCounts(res, ids.length);
          showBulkStatusToast(
            this.translate, (type, message) => this.showMsg(type, message),
            counts.requested, counts.succeeded,
            { docType: DocumentTypesUppercaseEnum.PO, action }
          );
        }
      });
  }

  onProceedBulkClick(): void {
    this.changePOStatus(PoTabs.Open, 'onProceedBulkClick');
  }

  onCancelBulkClick(): void {
    const count = this.bulkActionableSelectedRows.length;
    if (!count) {
      this.btnToClearLoadingStatus$.next('onCancelBulkClick');
      return;
    }

    const message = this.translate.instant('BULK.MODAL.CANCEL_MSG', {
      count,
      docType: getBulkDocTypeName(this.translate, DocumentTypesUppercaseEnum.PO, count),
    });

    this.confirmCancel(message, 'onCancelBulkClick', () => this.changePOStatus(PoTabs.Canceled, 'onCancelBulkClick'));
  }

  private confirmCancel(message: string, btnName: string, onConfirm: () => void): void {
    this.dialog.open(WarningModalComponent, {
      data: {
        title: 'MODAL.CANCEL',
        message,
        confirmBtnText: 'BUTTON.CANCEL_ENTITY',
        confirmBtnIcon: 'close-square'
      }
    }).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: CommonModalsActionsEnum) => {
        if (response === CommonModalsActionsEnum.CONFIRM) {
          onConfirm();
          return;
        }
        this.btnToClearLoadingStatus$.next(btnName);
      });
  }

  onReopenBulkClick(): void {
    this.changePOStatus(PoTabs.Draft, 'onReopenBulkClick');
  }

  onDeleteClick(): void {
    const dialog = this.dialog.open(DangerModalComponent, {
      data: getDeleteModalData(this.translate)
    });

    dialog.afterClosed()
      .subscribe((response: CommonModalsActionsEnum) => {
        if (response !== CommonModalsActionsEnum.CONFIRM) {
          this.btnToClearLoadingStatus$.next('onDeleteClick');
          return;
        }
        if (this.activeStatus === PoTabs.Template) {
          this.deletePermanently('onDeleteClick');
          return;
        }
        this.changePOStatus(PoTabs.Deleted, 'onDeleteClick');
      });
  }

  onDeletePermanentlyClick(): void {
    const dialog = this.dialog.open(DangerModalComponent, {
      data: getDeletePermanentlyModalData(this.translate)
    });

    dialog.afterClosed()
      .subscribe((response: CommonModalsActionsEnum) => {
        if (response === CommonModalsActionsEnum.CONFIRM) {
          this.deletePermanently('onDeletePermanentlyClick');
          return;
        }
        this.btnToClearLoadingStatus$.next('onDeletePermanentlyClick');
      });
  }

  public deletePermanently(btnName: string): void {
    const ids = this.bulkActionableSelectedRows.map((po: PurchaseOrder) => po.id);
    if (!ids.length) {
      this.btnToClearLoadingStatus$.next(btnName);
      return;
    }

    this.poApi.deletePOPermanentlyBatch(ids)
      .pipe(
        finalize(() => this.btnToClearLoadingStatus$.next(btnName)),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => {
        this.selectedRows = [];
        this.getPOTabsCounters();
        this.getPOList(true);
        const counts = getBulkResponseCounts(response, ids.length);
        showBulkStatusToast(
          this.translate, (type, message) => this.showMsg(type, message),
          counts.requested, counts.succeeded,
          { docType: DocumentTypesUppercaseEnum.PO, action: 'DELETED_PERMANENTLY' }
        );
      });
  }

  public sortTable(e): void {
    let sortedProp = e.column.prop;

    if (sortedProp === 'statusValue') { sortedProp = 'status'; }
    if (sortedProp === 'deliveryDateValue') { sortedProp = 'deliveryDate'; }
    if (sortedProp === 'documentLink') { sortedProp = 'runpleId'; }
    if (sortedProp === 'templateNameLink') { sortedProp = 'templateName'; }
    if (sortedProp === 'lastUsedAt') { sortedProp = 'lastTemplateUsedAt'; }
    if (sortedProp === 'vendor') { sortedProp = 'vendorName'; }

    this.sort = {nameColumn: sortedProp, value: e.newValue.toUpperCase()};
    this.saveUserSorting(sortedProp, e.newValue.toUpperCase());
  }

  public saveUserSorting(sortBy: string, direction: 'ASC'|'DESC'): void {
    const sorting = {
      sortBy,
      direction,
      type: DocumentTypesUppercaseEnum.PO,
      status: this.activeStatus
    }
    this.userSortingService.saveUserSorting(sorting)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.activatedRowReselect = ActivatedRowReselectEnum.NONE;
        this.getPOList(true);
      });
  }

  public onPageChange(page: number): void {
    if (page + 1 !== this.pagination.page) {
      this.autoSelectFirstRow = true;
    }
    this.goToPage(page);
  }

  public goToPage(page: number): void {
    const url = this.getNewPaginationUrl(this.router.url);

    this.pagination = { ...this.pagination, page: page + 1 };
    this.router.navigate([url, this.pagination.page], {queryParamsHandling: 'merge'});
  }

  public getNewPaginationUrl(oldUrl: string): string {
    return oldUrl.split('/').slice(0, -1).join('/');
  }


  private showMsg(type: string, message: string, status?: string): void {
    if (!status || status !== PoTabs.Deleted) {
      this.toasterService.notify({type, message});
    }
  }

  public get showSummaryBar(): boolean {
    return !!this.tableSummaryBarItems.length &&
      ( this.activeStatus === PoTabs.Draft ||
        this.activeStatus === PoTabs.Open ||
        this.activeStatus === PoTabs.All ||
        this.activeStatus === PoTabs.Completed ||
        this.activeStatus === PoTabs.Canceled ||
        this.activeStatus === PoTabs.Deleted
      );
  }

  public get activeStatus(): PoTabs { return this.activeStatus$.getValue(); }

  public get bulkSelectionAvailable(): boolean {
    return this.activeStatus === PoTabs.Draft ||
      this.activeStatus === PoTabs.Open ||
      this.activeStatus === PoTabs.Canceled ||
      this.activeStatus === PoTabs.Deleted ||
      this.activeStatus === PoTabs.Template;
  }

  public hasEditingFlag(row: PurchaseOrder): boolean {
    return !!(row && row.flags && row.flags.some((flag: string) => (flag || '').toUpperCase() === FlagsEnum.EDITING));
  }

  public hasCreationFlag(row: PurchaseOrder): boolean {
    return !!(row && !row.runpleId && row.status !== PoTabs.Template);
  }

  public showBillingDeliveryIndicators(row: PurchaseOrder): boolean {
    return !!row
      && row.status !== PoTabs.Draft
      && row.status !== PoTabs.Canceled
      && row.status !== PoTabs.Deleted;
  }

  public getBillingIconClass(row: PurchaseOrder): string | null {
    return getPurchaseOrderBillingIconClass(get(row, 'billing'));
  }

  public getDeliveryIconClass(row: PurchaseOrder): string | null {
    if (get(row, 'inventoryManagement') === false) { return null; }
    return getPurchaseOrderDeliveryIconClass(get(row, 'delivery'));
  }

  public getStatusLabel(rawStatus: string): string {
    if (!rawStatus) { return ''; }
    return this.translate.instant(this.statusTranslatePipe.transform(rawStatus));
  }

  public docStatusColorClass: { [status: string]: string } = {
    open: 'active',
    packing: 'active',
    packed_up: 'active',
    paid: 'positive',
    shipped: 'positive',
    canceled: 'negative',
    deleted: 'deleted',
  };

  public hoveredStatusIcon: PurchaseOrderStatusTooltipStateModel = null;
  public statusTooltipCache: { [key: string]: PurchaseOrderStatusTooltipViewModel } = {};
  public readonly statusTooltipMaxDocuments: number = STATUS_TOOLTIP_MAX_DOCUMENTS;

  private readonly statusTooltipHover$: Subject<PurchaseOrderStatusTooltipStateModel> =
    new Subject<PurchaseOrderStatusTooltipStateModel>();

  public onStatusIconMouseEnter(event: MouseEvent, row: PurchaseOrder, column: PurchaseOrderStatusTooltipColumn): void {
    const rect: ClientRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const isBilling: boolean = column === 'billing';
    const rawStatus: string = isBilling ? get(row, 'billing') : get(row, 'delivery');
    const flipUp: boolean = (window.innerHeight - rect.bottom) < STATUS_TOOLTIP_MAX_HEIGHT_PX;

    this.hoveredStatusIcon = {
      key: `${column}:${get(row, 'id')}`,
      row,
      column,
      titleKey: isBilling ? 'FORM.BILLING_STATUS' : 'FORM.DELIVERY_STATUS',
      doneLabelKey: isBilling ? 'PO.TOOLTIP_PAID' : 'PO.TOOLTIP_RECEIVED',
      docsTitleKey: isBilling ? 'PO.TOOLTIP_INVOICES' : null,
      docsEmptyKey: isBilling ? 'PO.TOOLTIP_NO_INVOICES' : null,
      statusLabel: this.getStatusLabel(rawStatus),
      top: flipUp ? rect.top : rect.bottom,
      left: this.clampStatusTooltipLeft(rect.left + rect.width / 2),
      flipUp,
    };

    this.statusTooltipHover$.next(this.hoveredStatusIcon);
  }

  public onStatusIconMouseLeave(): void {
    this.hoveredStatusIcon = null;
    this.statusTooltipHover$.next(null);
  }

  private clampStatusTooltipLeft(left: number): number {
    const min: number = STATUS_TOOLTIP_HALF_WIDTH_PX + STATUS_TOOLTIP_VIEWPORT_MARGIN_PX;
    const max: number = window.innerWidth - STATUS_TOOLTIP_HALF_WIDTH_PX - STATUS_TOOLTIP_VIEWPORT_MARGIN_PX;
    if (max < min) { return left; }
    return Math.min(Math.max(left, min), max);
  }

  private initStatusTooltipStream(): void {
    this.statusTooltipHover$
      .pipe(
        debounceTime(STATUS_TOOLTIP_HOVER_DELAY_MS),
        filter((hover: PurchaseOrderStatusTooltipStateModel) => !!hover && !this.statusTooltipCache[hover.key]),
        switchMap((hover: PurchaseOrderStatusTooltipStateModel) => {
          const request$: Observable<PurchaseOrderBillingTooltipResponse | PurchaseOrderDeliveryTooltipResponse> = hover.column === 'billing'
            ? this.poApi.getPurchaseOrderBillingTooltip(hover.row.id)
            : this.poApi.getPurchaseOrderDeliveryTooltip(hover.row.id);

          const fields = hover.column === 'billing' ? PO_BILLING_TOOLTIP_FIELDS : PO_DELIVERY_TOOLTIP_FIELDS;

          return request$.pipe(
            map((response) => ({ key: hover.key, data: mapPurchaseOrderStatusTooltip(response, fields) })),
            catchError(() => of({ key: hover.key, data: getEmptyPurchaseOrderStatusTooltip(true) })),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((result: { key: string; data: PurchaseOrderStatusTooltipViewModel }) => {
        this.statusTooltipCache[result.key] = result.data;
      }, () => {});
  }

  private clearStatusTooltipCache(): void {
    this.statusTooltipCache = {};
    this.onStatusIconMouseLeave();
  }

  @HostListener('window:scroll')
  public onWindowScrollHideStatusTooltip(): void {
    if (this.hoveredStatusIcon) { this.onStatusIconMouseLeave(); }
  }

  private get bulkActionableSelectedRows(): PurchaseOrder[] {
    return this.selectedRows.filter((po: PurchaseOrder) => !this.hasEditingFlag(po));
  }

  public get bulkActionsButtonsForStatus(): NavBarBtnModel[] {
    return this.bulkActionableSelectedRows.length ? this.actionBarButtons : [];
  }

  private isRowEditable(row: PurchaseOrder): boolean {
    return row.status !== PoTabs.Completed
      && row.status !== PoTabs.Canceled
      && row.status !== PoTabs.Deleted;
  }

  public get purchaseOrderPreviewActions(): ContextMenuItemModel[] {
    if (!this.activatedRow || !this.isRowEditable(this.activatedRow)) { return []; }

    return [
      { icon: 'edit', title: 'edit', action: ContextMenuItemAction.EDIT, disabled: false }
    ];
  }

  public onPreviewActionEmitted(action: string): void {
    if (!this.activatedRow) {
      this.btnToClearLoadingStatus$.next(action);
      return;
    }

    const documentId = this.activatedRow.id;

    switch (action) {
      case 'openDocument':
        this.router.navigate([`/trade/purchase-order/${documentId}`]);
        break;
      case ContextMenuItemAction.EDIT:
        if (!this.isRowEditable(this.activatedRow)) { return; }
        this.router.navigate(
          [`/trade/purchase-order/${documentId}`],
          { state: { setEditState: true, documentId } }
        );
        break;
      case 'onPreviewDeleteClick':
        this.onPreviewDeleteClick(action);
        break;
      case 'onPreviewDeletePermClick':
        this.onPreviewDeletePermClick(action);
        break;
      case 'onPreviewCancelPOClick':
        this.confirmCancel('PO.MODAL.CANCEL_MESSAGE', action, () => this.previewChangeStatus(PoTabs.Canceled, documentId, action));
        break;
      case 'onPreviewClonePOClick':
        this.onPreviewClonePOClick(action);
        break;
      case 'onPreviewCreateSalesOrderClick':
        this.onPreviewCreateSalesOrderClick(action);
        break;
      case 'onPreviewRestoreClick':
        this.previewChangeStatus(PoTabs.Draft, documentId, action);
        break;
      case 'onPreviewProceedClick':
        this.previewChangeStatus(PoTabs.Open, documentId, action);
        break;
      case 'onPreviewProductReceiptClick':
        this.onPreviewProductReceiptClick(action);
        break;
      case 'onPreviewCreateInvoiceClick':
        this.onPreviewCreateInvoiceClick(action);
        break;
      default:
        this.btnToClearLoadingStatus$.next(action);
        break;
    }
  }

  private previewChangeStatus(status: PoTabs, poId: number, action: string): void {
    this.poApi.changePurchaseOrdersStatus(status, poId)
      .pipe(
        finalize(() => this.btnToClearLoadingStatus$.next(action)),
        catchError((error: HttpErrorResponse) => {
          this.handlePreviewPopupErrors(error.error.message);
          return throwError(error);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (status === PoTabs.Open) {
          this.showMsg('success', getDocumentCreatedToastKey(DocumentTypesUppercaseEnum.PO));
        }
        this.getPOList(true);
        this.getPOTabsCounters();
      });
  }

  private handlePreviewPopupErrors(error: string): void {
    switch (error) {
      case 'poForUnitsAlreadyCreated':
        this.dialog.open(InfoModalComponent, { data: CannotProceedErrModalData });
        break;
      default:
        this.showMsg('error', error);
        break;
    }
  }

  private onPreviewDeleteClick(action: string): void {
    const row = this.activatedRow;
    const data = row.status === PoTabs.Template
      ? DeleteTemplateModalData
      : { title: 'MODAL.DELETE', message: 'PO.MODAL.DELETE_PO_MESSAGE', confirmBtnText: 'BUTTON.DELETE', confirmBtnIcon: 'trash-2' };

    this.dialog.open(DangerModalComponent, { data }).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: CommonModalsActionsEnum) => {
        if (response === CommonModalsActionsEnum.CONFIRM) {
          if (row.status === PoTabs.Template) {
            this.onPreviewDeletePermanently(row.id, action);
            return;
          }
          this.previewChangeStatus(PoTabs.Deleted, row.id, action);
          return;
        }
        this.btnToClearLoadingStatus$.next(action);
      });
  }

  private onPreviewDeletePermClick(action: string): void {
    const poId = this.activatedRow.id;
    this.dialog.open(DangerModalComponent, {
      data: {
        title: 'MODAL.DELETE_PERMANENTLY',
        message: 'PO.MODAL.DELETE_PERMANENTLY_MESSAGE',
        confirmBtnText: 'BUTTON.DELETE',
        confirmBtnIcon: 'trash-2'
      }
    }).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response: CommonModalsActionsEnum) => {
        if (response === CommonModalsActionsEnum.CONFIRM) {
          this.onPreviewDeletePermanently(poId, action);
          return;
        }
        this.btnToClearLoadingStatus$.next(action);
      });
  }

  private onPreviewDeletePermanently(poId: number, action: string): void {
    this.poApi.deletePOPermanentlyBatch([poId])
      .pipe(
        finalize(() => this.btnToClearLoadingStatus$.next(action)),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.getPOList(true);
        this.getPOTabsCounters();
      });
  }

  private onPreviewClonePOClick(action: string): void {
    this.poApi.clonePO(this.activatedRow.id)
      .pipe(
        finalize(() => this.btnToClearLoadingStatus$.next(action)),
        takeUntil(this.destroy$)
      )
      .subscribe((purchaseOrder: PurchaseOrder) => {
        this.router.navigate(['/trade/purchase-order', purchaseOrder.id]);
      });
  }

  private onPreviewCreateSalesOrderClick(action: string): void {
    this.dialog.open(CreateSoModalComponent, {
      data: { poId: this.activatedRow.id },
      panelClass: 'mat-dialog-container--w-full',
      maxWidth: '1360px'
    }).afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((salesOrder: SalesOrderModel) => {
        this.btnToClearLoadingStatus$.next(action);
        if (salesOrder) {
          this.router.navigate(['/trade/sales-order', salesOrder.id]);
        }
      });
  }

  private onPreviewProductReceiptClick(action: string): void {
    if (this.previewConfirmReceiptRequest$.getValue()) {
      this.btnToClearLoadingStatus$.next(action);
      return;
    }
    this.previewConfirmReceiptRequest$.next(true);

    this.dialog.open(IncomingDeliveryProductsReceiptModalComponent, {
      data: { poId: this.activatedRow.id, fromPo: true },
      disableClose: true,
      panelClass: 'mat-dialog-container--w-full',
      maxWidth: '1500px'
    }).afterClosed()
      .pipe(
        finalize(() => {
          this.previewConfirmReceiptRequest$.next(false);
          this.btnToClearLoadingStatus$.next(action);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.getPOList(true));
  }

  private onPreviewCreateInvoiceClick(action: string): void {
    if (this.previewLinkIncomingInvoiceRequest$.getValue()) {
      this.btnToClearLoadingStatus$.next(action);
      return;
    }
    this.previewLinkIncomingInvoiceRequest$.next(true);

    const poId = this.activatedRow.id;
    this.invoiceApiService.createInvoiceFromPO(poId)
      .pipe(
        finalize(() => {
          this.previewLinkIncomingInvoiceRequest$.next(false);
          this.btnToClearLoadingStatus$.next(action);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((incomingInvoice) => {
        this.showMsg('success', 'IIN.CREATED_SUCCESS');
        this.router.navigate(
          [`/accounting/incoming-invoice/${incomingInvoice.invoiceId}`],
          { state: { uiState: UIStatesEnum.CREATE } }
        );
      }, (error: HttpErrorResponse) => {
        this.showMsg('error', error.error.message || error.error.errors);
      });
  }

  private computePreviewFooterActionsForStatus(): NavBarBtnModel[] {
    if (!this.activatedRow) { return this.previewFooterActions; }
    if (this.hasEditingFlag(this.activatedRow)) {
      return this.isRowEditable(this.activatedRow) ? getEditOnlyPreviewFooterActions() : [];
    }

    const row = this.activatedRow;
    const createButtons = (row.status as PoTabs) === PoTabs.Draft ? [this.previewCreateButton] : [];

    if (this.hasCreationFlag(row)) {
      return [...(this.isRowEditable(row) ? getEditOnlyPreviewFooterActions() : []), ...createButtons];
    }

    const isOpen = (row.status as PoTabs) === PoTabs.Open;

    const showGoodsReceipt = isOpen && !!get(row, 'hasResaleGoods') && !!get(row, 'allowedProductReceipt');
    const showCreateInvoice = isOpen && !!get(row, 'allowedIinCreation');

    const footerButtons = this.previewFooterActions
      .map(btn => btn.actionName === 'onPreviewProductReceiptClick'
        ? this.getPreviewMainAction(btn, showGoodsReceipt, showCreateInvoice)
        : btn)
      .filter(Boolean);
    return [...collapseMoreActionsIntoFooter(footerButtons, this.moreActionsForActivatedRow), ...createButtons];
  }

  private readonly previewCreateButton: NavBarBtnModel = {
    classes: 'btn-primary',
    iconName: 'plus-square',
    isMoreAction: true,
    actions: [{
      actionTitle: 'BUTTON.CREATE',
      actionName: 'onPreviewProceedClick',
      actionIcon: 'plus-square',
      classes: 'btn-primary',
    }],
  };

  private getPreviewMainAction(receiptBtn: NavBarBtnModel, showGoodsReceipt: boolean, showCreateInvoice: boolean): NavBarBtnModel | null {
    if (showGoodsReceipt && showCreateInvoice) { return receiptBtn; }

    const singleAction = showGoodsReceipt
      ? receiptBtn.actions.find(action => action.actionName === 'onPreviewProductReceiptClick')
      : showCreateInvoice
        ? receiptBtn.actions.find(action => action.actionName === 'onPreviewCreateInvoiceClick')
        : null;
    if (!singleAction) { return null; }

    return {
      classes: 'btn-white', iconName: singleAction.actionIcon, isMoreAction: true,
      actions: [{ ...singleAction, classes: 'btn-white' }],
    };
  }

  private get moreActionsForActivatedRow(): { actionTitle: string; actionName: string; actionIcon: string; classes?: string; disabled?: boolean }[] {
    const row = this.activatedRow;
    if (!row) { return []; }

    const status = row.status as PoTabs;

    if (status === PoTabs.Template) { return []; }

    const allowedSoCreation = !!get(row, 'allowedSoCreation');

    const actions = [
      {
        actionTitle: 'BUTTON.DELETE', actionName: 'onPreviewDeleteClick', actionIcon: 'trash-2',
        classes: 'btn-white',
        hideWithStatus: [PoTabs.Open, PoTabs.Completed, PoTabs.Deleted],
      },
      {
        actionTitle: 'BUTTON.DELETE_PERMANENTLY', actionName: 'onPreviewDeletePermClick', actionIcon: 'fire',
        classes: 'btn-white',
        hideWithStatus: [PoTabs.Draft, PoTabs.Open, PoTabs.Canceled, PoTabs.Completed],
      },
      {
        actionTitle: 'BUTTON.CANCEL_ENTITY', actionName: 'onPreviewCancelPOClick', actionIcon: 'close',
        classes: 'btn-white',
        hideWithStatus: [PoTabs.Draft, PoTabs.Completed, PoTabs.Canceled, PoTabs.Deleted],
      },
      {
        actionTitle: 'BUTTON.CLONE', actionName: 'onPreviewClonePOClick', actionIcon: 'copy',
        classes: 'btn-white',
        hideWithStatus: [PoTabs.Canceled, PoTabs.Deleted],
      },
      {
        actionTitle: 'SO_PO.CREATE_SO', actionName: 'onPreviewCreateSalesOrderClick', actionIcon: 'sales-order',
        classes: 'btn-white', showCondition: allowedSoCreation,
        hideWithStatus: [PoTabs.Draft, PoTabs.Canceled, PoTabs.Deleted],
      },
      {
        actionTitle: 'BUTTON.REOPEN', actionName: 'onPreviewRestoreClick', actionIcon: 'undo',
        classes: 'btn-white',
        hideWithStatus: [PoTabs.Draft, PoTabs.Open, PoTabs.Completed],
      },
    ];

    return actions
      .filter(action => !action.hasOwnProperty('showCondition') || action.showCondition)
      .filter(a => !a.hideWithStatus.includes(status))
      .map(({ hideWithStatus, showCondition, ...action }) => action);
  }

  ngOnDestroy(): void {
    this.statusTooltipHover$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
